import React from "react";
import LegalPage from "@/components/LegalPage";

export default function Privacy() {
  return (
    <LegalPage
      title="Privacy Policy"
      lastUpdated="May 21, 2026"
      testid="privacy-page"
    >
      <p>
        Colleague is built around a simple promise: your conversations are yours.
        This policy explains what we collect, why, and what we never do with it.
        We've kept it short and in plain language on purpose.
      </p>

      <h2 className="font-serif text-2xl text-[#2D2926] mt-10">
        What we collect
      </h2>
      <p>When you use Colleague, we store:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Your name and email address (so you can sign in)</li>
        <li>Your password, salted and hashed (we cannot see it in plain text)</li>
        <li>The profile fields you choose to share (job title, industry, hobbies, etc. — all optional)</li>
        <li>The personas you create and the conversations you have with them</li>
        <li>Your mood ("Vibe") entries when you record them</li>
        <li>Voice notes you record, for the purpose of transcription only — the audio is processed and discarded</li>
        <li>Payment metadata from Stripe (we never see your card number)</li>
      </ul>

      <h2 className="font-serif text-2xl text-[#2D2926] mt-10">
        What we DO NOT do
      </h2>
      <ul className="list-disc pl-6 space-y-1">
        <li>We do not sell your data. Ever. To anyone.</li>
        <li>We do not use your conversations to train AI models.</li>
        <li>We do not share your data with advertisers or data brokers.</li>
        <li>We do not read your conversations except as required for technical support, and only when you ask us to.</li>
        <li>We do not show your data to your employer, ever — Colleague is yours, not theirs.</li>
      </ul>

      <h2 className="font-serif text-2xl text-[#2D2926] mt-10">
        Who processes your data
      </h2>
      <p>
        Colleague relies on a small set of trusted infrastructure providers to
        operate. Each is bound by their own privacy and security commitments:
      </p>
      <ul className="list-disc pl-6 space-y-1">
        <li>
          <strong>Anthropic (via Emergent)</strong> — processes your messages to generate persona replies. Anthropic does not retain or train on these messages.
        </li>
        <li>
          <strong>OpenAI (via Emergent)</strong> — transcribes voice notes (Whisper). Audio is not retained.
        </li>
        <li>
          <strong>Google Gemini (via Emergent)</strong> — generates AI avatars when you ask for one.
        </li>
        <li>
          <strong>Stripe</strong> — processes payments and stores your billing info. Stripe is PCI-DSS certified.
        </li>
        <li>
          <strong>Giphy</strong> — sources cute animal clips. Giphy may set cookies on the images served.
        </li>
        <li>
          <strong>Resend</strong> — sends transactional emails (trial reminders, account updates).
        </li>
        <li>
          <strong>MongoDB Atlas</strong> — stores your account and conversation data, encrypted in transit and at rest.
        </li>
      </ul>

      <h2 className="font-serif text-2xl text-[#2D2926] mt-10">
        Cookies and tracking
      </h2>
      <p>
        We use a single session cookie to keep you signed in. We don't use
        third-party analytics or advertising cookies on Colleague itself.
      </p>

      <h2 className="font-serif text-2xl text-[#2D2926] mt-10">
        Your rights
      </h2>
      <p>You can, at any time:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Export your conversations (one click in any conversation view)</li>
        <li>Update or remove your profile fields</li>
        <li>Delete your account (email us — see <a href="/contact" className="text-[#D4A574] hover:text-[#C99560] underline">Contact</a>)</li>
        <li>Request a copy of all data we hold about you</li>
      </ul>
      <p>
        We comply with GDPR and CCPA principles by default. If you're in the EU
        or California and want a formal data request, email us at the address
        on the Contact page.
      </p>

      <h2 className="font-serif text-2xl text-[#2D2926] mt-10">
        Data retention
      </h2>
      <p>
        Your conversations stay in your account until you delete them. If you
        cancel your subscription, we keep your data so you can come back. If
        you delete your account, we permanently remove your conversations and
        profile within 30 days, except where law requires us to keep payment
        records longer.
      </p>

      <h2 className="font-serif text-2xl text-[#2D2926] mt-10">
        Children
      </h2>
      <p>
        Colleague is built for working adults. We do not knowingly collect
        information from anyone under 16. If you believe a minor has registered,
        email us and we'll delete the account.
      </p>

      <h2 className="font-serif text-2xl text-[#2D2926] mt-10">
        Changes to this policy
      </h2>
      <p>
        If we make material changes, we'll email you and post a note in the app
        at least 14 days before they take effect.
      </p>

      <h2 className="font-serif text-2xl text-[#2D2926] mt-10">
        Contact
      </h2>
      <p>
        Questions or concerns? Email us at{" "}
        <a
          href="mailto:hello@meetacolleague.com"
          className="text-[#D4A574] hover:text-[#C99560] underline"
        >
          hello@meetacolleague.com
        </a>{" "}
        and we'll respond within 3 business days.
      </p>
    </LegalPage>
  );
}
